import {
  ZodDefault,
  ZodEffects,
  ZodEnum,
  ZodLiteral,
  ZodNullable,
  ZodNumber,
  ZodObject,
  ZodOptional,
  ZodRawShape,
  ZodString,
  ZodTypeAny,
  z,
} from 'zod';
import { ZodRefinement } from './types';
import { extendApi } from '../openapi';

export type ZodStringish =
  | ZodString
  | ZodEnum<[string, ...string[]]>
  | ZodLiteral<string>
  | ZodOptional<ZodString>
  | ZodDefault<ZodString>
  | ZodNullable<ZodString>;

const isoDateRegex = /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)/; // https://stackoverflow.com/a/3143231
export const isoDateRefinement: ZodRefinement<string> = [
  val => isoDateRegex.test(val) && !isNaN(Date.parse(val)),
  { message: 'date.iso' },
];

export interface ZodUrl {
  scheme?: ZodStringish;
  username?: ZodStringish;
  password?: ZodStringish;
  host?: ZodStringish;
  hostname?: ZodStringish;
  port?: ZodNumber;
  hash?: ZodStringish;
  search?: ZodStringish;
  searchParams?: ZodObject<ZodRawShape>;
}

export function urlRefinement(urlType?: ZodUrl): ZodRefinement<string> {
  return [
    val => {
      try {
        const url = new URL(val);
        z.object(urlType as ZodRawShape).parse({
          ...url,
          scheme: url.protocol.substring(0, url.protocol.length - 1),
          port: url.port ? parseInt(url.port, 10) : undefined,
          searchParams: Object.fromEntries(url.searchParams.entries()),
        });

        return true;
      } catch {
        return false;
      }
    },
    { message: 'string.url' },
  ];
}

export const uuid = extendApi(z.string().uuid(), {
  format: 'uuid',
  example: 'a1b2c3d4-e5f6-7a8b-9c0d-1e2f3a4b5c6d',
});

export const isoDateString = extendApi(z.string().refine(...isoDateRefinement), {
  format: 'date-time',
  description: 'Date string following the ISO 8601 format',
  example: '2021-01-01T00:00:00.000Z',
});

export const language = extendApi(
  z
    .string()
    .length(2)
    .transform(str => str.toLowerCase()),
  {
    type: 'string',
    description: 'Language code following the ISO 639-1 format',
    examples: ['en', 'fr'],
    minLength: 2,
    maxLength: 2,
  },
);

export const rabbitmqUrl = extendApi(
  z.string().refine(...urlRefinement({ scheme: z.enum(['amqp', 'amqps']) })),
  {
    format: 'uri',
    description: 'RabbitMQ URL',
    example: 'amqp://localhost?heartbeat=15',
  },
);

export const postgresUrl = extendApi(
  z.string().refine(...urlRefinement({ scheme: z.literal('postgres') })),
  {
    format: 'uri',
    description: 'PostgreSQL URL',
    example: 'postgres://user:password@localhost:5432/dbname',
  },
);

export const httpUrl = extendApi(
  z.string().refine(...urlRefinement({ scheme: z.enum(['https', 'http']) })),
  {
    format: 'uri',
    description: 'HTTP URL',
    example: 'https://example.com',
  },
);

export const keycloakUrl = extendApi(httpUrl.describe('Keycloak URL'), {
  format: 'uri',
  example: 'https://keycloak.example.com',
});

export function emptyStringToUndefined<I extends ZodTypeAny = ZodString>(
  type: I = z.string() as unknown as I,
): ZodEffects<I, I['_output'], unknown> {
  return extendApi(
    z.preprocess(val => {
      if (!val || val.toString().trim() === '') {
        return undefined;
      }
      return val;
    }, type),
    {
      type: 'string', // FIXME ZodType to type
    },
  );
}
